import axios from "axios";

// Base URL config
const API_BASE_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8000/api";

// Strategy Engine API (Cloudflare Worker)
const STRATEGY_ENGINE_URL =
  process.env.REACT_APP_STRATEGY_ENGINE_URL ||
  "https://strategy-engine.houman-ghavamzadeh.workers.dev";

// Create axios instance with default config
const API = axios.create({
  baseURL: API_BASE_URL,
  timeout: 30000, // 30 seconds timeout
  headers: {
    "Content-Type": "application/json",
  },
});

// Create Strategy Engine axios instance
const StrategyEngineAPI = axios.create({
  baseURL: STRATEGY_ENGINE_URL,
  timeout: 30000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor for adding auth token
API.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("auth_token");
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

// Response interceptor for handling common errors
API.interceptors.response.use(
  (response) => response,
  (error) => {
    const { status } = error.response || {};

    // Handle authentication errors
    if (status === 401) {
      // Clear token and redirect to login
      localStorage.removeItem("auth_token");
      window.location.href = "/login";
    }

    // Handle server errors
    if (status >= 500) {
      console.error(
        "Server error occurred:",
        error.response?.data || error.message,
      );
      // You could dispatch to an error notification system here
    }

    return Promise.reject(error);
  },
);

// API endpoint functions organized by domain
const AuthAPI = {
  login: (credentials) => API.post("/auth/login", credentials),
  register: (userData) => API.post("/auth/register", userData),
  logout: () => API.post("/auth/logout"),
  refresh: () => API.post("/auth/refresh"),
  forgotPassword: (email) => API.post("/auth/forgot-password", { email }),
  resetPassword: (token, newPassword) =>
    API.post("/auth/reset-password", { token, newPassword }),
};

const UserAPI = {
  getCurrentUser: () => API.get("/user/profile"),
  updateProfile: (data) => API.put("/user/profile", data),
  updateSettings: (settings) => API.put("/user/settings", settings),
  getNotifications: () => API.get("/user/notifications"),
  markNotificationRead: (id) => API.put(`/user/notifications/${id}/read`),
};

const TradingAPI = {
  getStrategies: () => API.get("/trading/strategies"),
  createStrategy: (data) => API.post("/trading/strategies", data),
  updateStrategy: (id, data) => API.put(`/trading/strategies/${id}`, data),
  deleteStrategy: (id) => API.delete(`/trading/strategies/${id}`),
  executeStrategy: (id, params) =>
    API.post(`/trading/strategies/${id}/execute`, params),
  getTradeHistory: (filters) =>
    API.get("/trading/history", { params: filters }),
  getTechnicalIndicators: (symbol, params) =>
    API.get(`/trading/technical/${symbol}`, { params }),
  getFundamentalData: (symbol) => API.get(`/trading/fundamental/${symbol}`),
};

const AdminAPI = {
  getUsers: (params) => API.get("/admin/users", { params }),
  updateUser: (id, data) => API.put(`/admin/users/${id}`, data),
  deleteUser: (id) => API.delete(`/admin/users/${id}`),
  getTickets: (params) => API.get("/admin/tickets", { params }),
  updateTicket: (id, data) => API.put(`/admin/tickets/${id}`, data),
  getPlans: () => API.get("/admin/plans"),
  createPlan: (data) => API.post("/admin/plans", data),
  updatePlan: (id, data) => API.put(`/admin/plans/${id}`, data),
  deletePlan: (id) => API.delete(`/admin/plans/${id}`),
  getPayments: (params) => API.get("/admin/payments", { params }),
  getBlogPosts: (params) => API.get("/admin/blog", { params }),
  createBlogPost: (data) => API.post("/admin/blog", data),
  updateBlogPost: (id, data) => API.put(`/admin/blog/${id}`, data),
  deleteBlogPost: (id) => API.delete(`/admin/blog/${id}`),
  getNewsletter: () => API.get("/admin/newsletter"),
  sendNewsletter: (data) => API.post("/admin/newsletter/send", data),
  getMarketingCampaigns: (params) =>
    API.get("/admin/marketing/campaigns", { params }),
  createMarketingCampaign: (data) =>
    API.post("/admin/marketing/campaigns", data),
  updateMarketingCampaign: (id, data) =>
    API.put(`/admin/marketing/campaigns/${id}`, data),
  deleteMarketingCampaign: (id) =>
    API.delete(`/admin/marketing/campaigns/${id}`),
};

// Strategy Engine API endpoints (connected to live Cloudflare Worker)
const StrategyAPI = {
  // System
  getHealth: () => StrategyEngineAPI.get("/health"),
  getSystemState: () => StrategyEngineAPI.get("/api/system/state"),

  // Strategies
  getStrategies: () => StrategyEngineAPI.get("/api/strategies"),
  getStrategy: (id) => StrategyEngineAPI.get(`/api/strategies/${id}`),
  createStrategy: (data) => StrategyEngineAPI.post("/api/strategies", data),
  updateStrategy: (id, data) =>
    StrategyEngineAPI.put(`/api/strategies/${id}`, data),
  deleteStrategy: (id) => StrategyEngineAPI.delete(`/api/strategies/${id}`),

  // Runs
  getRuns: (params) => StrategyEngineAPI.get("/api/runs", { params }),
  getRun: (id) => StrategyEngineAPI.get(`/api/runs/${id}`),
  startRun: (strategyId, params) =>
    StrategyEngineAPI.post(`/api/runs`, { strategyId, ...params }),
  pauseRun: (id) => StrategyEngineAPI.post(`/api/runs/${id}/pause`),
  resumeRun: (id) => StrategyEngineAPI.post(`/api/runs/${id}/resume`),
  stopRun: (id) => StrategyEngineAPI.post(`/api/runs/${id}/stop`),

  // Orders & Trades
  getOrders: (params) => StrategyEngineAPI.get("/api/orders", { params }),
  getTrades: (params) => StrategyEngineAPI.get("/api/trades", { params }),
  getPositions: (params) => StrategyEngineAPI.get("/api/positions", { params }),

  // Schedules
  getSchedules: (params) => StrategyEngineAPI.get("/api/schedules", { params }),
  createSchedule: (data) => StrategyEngineAPI.post("/api/schedules", data),
  deleteSchedule: (id) => StrategyEngineAPI.delete(`/api/schedules/${id}`),
};

// Strategy Engine Admin API (requires X-Admin-Key header)
const StrategyAdminAPI = {
  setAdminKey: (key) => {
    StrategyEngineAPI.defaults.headers["X-Admin-Key"] = key;
  },

  // System State
  getFullState: () => StrategyEngineAPI.get("/api/admin/system/state"),
  startSystem: (reason) =>
    StrategyEngineAPI.post("/api/admin/system/start", { reason }),
  pauseSystem: (reason) =>
    StrategyEngineAPI.post("/api/admin/system/pause", { reason }),

  // Kill Switch
  activateKillSwitch: (reason) =>
    StrategyEngineAPI.post("/api/admin/kill-switch", { reason }),
  getKillSwitchStatus: () =>
    StrategyEngineAPI.get("/api/admin/kill-switch/status"),
  clearKillSwitch: (reason) =>
    StrategyEngineAPI.post("/api/admin/kill-switch/clear", { reason }),

  // Safety Limits
  getSafetyLimits: () => StrategyEngineAPI.get("/api/admin/safety-limits"),
  updateSafetyLimits: (limits) =>
    StrategyEngineAPI.put("/api/admin/safety-limits", limits),

  // Daily Stats
  getDailyStats: () => StrategyEngineAPI.get("/api/admin/daily-stats"),
  resetDailyStats: () => StrategyEngineAPI.post("/api/admin/daily-stats/reset"),

  // Health & Diagnostics
  getHealth: () => StrategyEngineAPI.get("/api/admin/health"),
  getFullHealth: () => StrategyEngineAPI.get("/api/admin/health/full"),
  getAuditLog: (params) =>
    StrategyEngineAPI.get("/api/admin/audit-log", { params }),

  // Reconciliation
  runReconciliation: () =>
    StrategyEngineAPI.post("/api/admin/reconciliation/run"),
  getReconciliationStatus: () =>
    StrategyEngineAPI.get("/api/admin/reconciliation/status"),

  // Cooldowns
  getCooldowns: () => StrategyEngineAPI.get("/api/admin/cooldowns"),
  resetCooldowns: () => StrategyEngineAPI.post("/api/admin/cooldowns/reset"),

  // Rate Limits
  getRateLimits: () => StrategyEngineAPI.get("/api/admin/rate-limits"),
};

export {
  API,
  StrategyEngineAPI,
  AuthAPI,
  UserAPI,
  TradingAPI,
  AdminAPI,
  StrategyAPI,
  StrategyAdminAPI,
};
